/*
 * Product card behavior for snippets/excel-product-card.liquid (Project Essentials and
 * collection results), plus the Rapid Search bridge that swaps Rapid's collection cards
 * for theme-rendered cards from templates/product.excel-card.liquid.
 * Cart-aware stock limits stay in sections/excel-cart-popup.liquid.
 */
if (!customElements.get('excel-product-card')) {
  customElements.define('excel-product-card', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener('click', event => this.onClick(event));
      this.querySelectorAll('.excel-essentials__quantity').forEach(control => this.bindQuantity(control));
    }

    disconnectedCallback() { this.toggleInfo(false); }

    bindQuantity(control) {
      const input = control.querySelector('input');
      if (input.disabled) return;
      const minimum = Number(input.min) || 1;
      const increment = Number(input.step) || 1;
      const maximum = () => input.max ? minimum + Math.floor((Number(input.max) - minimum) / increment) * increment : Infinity;
      const decrease = control.querySelector('[data-quantity-step="decrease"]');
      const increase = control.querySelector('[data-quantity-step="increase"]');
      const normalize = value => Math.min(maximum(), Math.max(minimum, minimum + Math.round((value - minimum) / increment) * increment));
      const update = () => {
        const value = input.valueAsNumber;
        decrease.disabled = input.disabled || !Number.isFinite(value) || value <= minimum;
        const atLimit = input.disabled || Number.isFinite(maximum()) && value >= maximum();
        if (input.form?.querySelector('[data-stock-product]')) {
          increase.disabled = false;
          increase.dataset.atLimit = String(atLimit);
        } else increase.disabled = atLimit;
      };
      control.addEventListener('click', event => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button || button.disabled) return;
        const current = Number.isFinite(input.valueAsNumber) ? normalize(input.valueAsNumber) : minimum;
        input.value = String(normalize(current + (button.dataset.quantityStep === 'increase' ? increment : -increment)));
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      input.addEventListener('input', update);
      input.addEventListener('change', update);
      update();
    }

    onClick(event) {
      const toggle = event.target.closest('[data-price-info-toggle]');
      if (toggle) {
        this.toggleInfo(toggle.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const option = event.target.closest('[data-essentials-variant]');
      if (!option) return;
      const form = option.closest('form');
      if (option.getAttribute('aria-disabled') === 'true') {
        this.jiggle(option, event.detail === 0);
        return;
      }
      const stock = JSON.parse(option.dataset.variantStock);
      const id = form.elements.namedItem('id');
      const quantity = form.elements.namedItem('quantity');
      id.value = String(stock.id);
      quantity.value = String(stock.min);
      form.querySelector('[data-essentials-selection-controls]').hidden = false;
      form.querySelectorAll('[data-essentials-variant]').forEach(choice => choice.setAttribute('aria-pressed', String(choice === option)));
      form.querySelector('[data-essentials-options-label]').textContent = option.dataset.variantTitle;
      form.querySelector('[data-essentials-options]').open = false;
      this.querySelector('[data-essentials-price]').textContent = option.dataset.variantPrice;
      const from = this.querySelector('[data-essentials-from]');
      if (from) from.hidden = true;
      const part = this.querySelector('[data-essentials-part-number]');
      if (part) part.textContent = option.dataset.variantPart;
      id.dispatchEvent(new Event('change', { bubbles: true }));
    }

    toggleInfo(open) {
      const info = this.querySelector('[data-price-info]');
      const toggle = info?.querySelector('[data-price-info-toggle]');
      if (!toggle) return;
      info.toggleAttribute('data-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      this.infoController?.abort();
      if (!open) return;
      this.infoController = new AbortController();
      const options = { signal: this.infoController.signal };
      document.addEventListener('click', event => { if (!info.contains(event.target)) this.toggleInfo(false); }, { ...options, capture: true });
      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        this.toggleInfo(false);
        toggle.focus();
      }, options);
    }

    jiggle(element, keyboard = false) {
      if (keyboard || element.classList.contains('excel-essentials__is-jiggling')) return;
      element.classList.add('excel-essentials__is-jiggling');
      setTimeout(() => element.classList.remove('excel-essentials__is-jiggling'), 280);
    }
  });
}

(() => {
  const start = () => {
    const results = document.querySelector('.rps-results');
    if (!results || !document.getElementById('rpsCollectionInfo') || results.dataset.excelCards) return;
    results.dataset.excelCards = 'true';
    const root = window.Shopify?.routes?.root || '/';
    const requests = new Map();
    const request = url => {
      if (!requests.has(url)) {
        requests.set(url, fetch(url, { credentials: 'same-origin' }).then(response => {
          if (!response.ok) throw new Error(`Card request failed: ${response.status}`);
          return response.text();
        }).catch(error => {
          requests.delete(url);
          throw error;
        }));
      }
      return requests.get(url);
    };
    const hydrate = container => {
      const id = container.dataset.productId;
      const link = container.querySelector(':scope > .rps-product-link');
      if (!id || !link) return;
      const current = container.querySelector(':scope > excel-product-card');
      if (current?.dataset.productId === id || container.dataset.excelCardPending === id) return;
      if (current) {
        current.remove();
        container.classList.remove('is-excel-card');
      }
      const handle = new URL(link.href, location.origin).pathname.match(/\/products\/([^/?#]+)/)?.[1];
      if (!handle) return;
      container.dataset.excelCardPending = id;
      container.classList.add('is-excel-card-loading');
      request(`${root}products/${handle}?view=excel-card`).then(html => {
        if (container.dataset.productId !== id) return;
        const template = document.createElement('template');
        template.innerHTML = html;
        const card = template.content.querySelector('excel-product-card');
        if (!card) throw new Error('Card markup missing');
        container.querySelector(':scope > excel-product-card')?.remove();
        container.append(card);
        container.classList.add('is-excel-card');
      }).catch(() => {
        if (container.dataset.productId !== id) return;
        container.querySelector(':scope > excel-product-card')?.remove();
        container.classList.remove('is-excel-card');
      }).finally(() => {
        if (container.dataset.excelCardPending !== id) return;
        delete container.dataset.excelCardPending;
        container.classList.remove('is-excel-card-loading');
      });
    };
    let queued = false;
    const scan = () => {
      queued = false;
      results.querySelectorAll('.rps-product-container[data-product-id]').forEach(hydrate);
    };
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      setTimeout(scan, 30);
    }).observe(results, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-product-id'] });
    scan();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
